import { ReactNode, useRef, useEffect } from 'react'
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '@/store/authStore'
import BottomNavigation from './BottomNavigation'
import { IoSettingsOutline, IoLogOutOutline } from 'react-icons/io5'
import ActiveBrandLabel from '../brands/ActiveBrandLabel'
import { useBrandStore } from '@/store/brandStore'
import { useQuery } from '@tanstack/react-query'
import { getUserSelectedBrandId, setUserSelectedBrandId } from '@/lib/queries/userPreferences'

interface MobileLayoutProps {
  children: ReactNode
}

/**
 * Mobile-first layout optimized for iPad Mini
 * - Full viewport height
 * - Bottom navigation for thumb reach
 * - Safe area insets support
 * - Allows pages to control their own layout
 * - Scrolls to top on navigation (accounting for sticky header)
 */
export default function MobileLayout({ children }: MobileLayoutProps) {
  const { user, logout } = useAuthStore()
  const queryClient = useQueryClient()
  const location = useLocation()
  const { brandId } = useParams<{ brandId?: string }>()
  const [searchParams] = useSearchParams()
  const brandIdFromQuery = searchParams.get('brandId') || undefined
  const { selectedBrandId, setSelectedBrandId, clearSelectedBrandId } = useBrandStore()

  const activeBrandId = brandId || brandIdFromQuery || selectedBrandId || undefined
  const scrollableRef = useRef<HTMLDivElement>(null)
  const lastPersistedRef = useRef<string | null>(null)
  const { data: persistedBrandId } = useQuery({
    queryKey: ['userPreference', 'selectedBrand', user?._id || 'anon'],
    queryFn: () => getUserSelectedBrandId(user!._id),
    enabled: !!user?._id,
    staleTime: 60_000,
  })

  // Scroll to top when route changes, accounting for sticky header
  useEffect(() => {
    if (scrollableRef.current) {
      // Header is sticky, so we scroll to 0 (top of scrollable container)
      // The header will naturally overlay, but content starts below it
      scrollableRef.current.scrollTo({ top: 0, behavior: 'instant' })
    }
  }, [location.pathname])

  // Keep selected brand in memory so it remains visible across routes
  useEffect(() => {
    if (brandId) {
      setSelectedBrandId(brandId)
    } else if (brandIdFromQuery) {
      setSelectedBrandId(brandIdFromQuery)
    }
  }, [brandId, brandIdFromQuery, setSelectedBrandId])

  // Hydrate brand selection from per-user preference when route/query does not specify one.
  useEffect(() => {
    if (brandId || brandIdFromQuery) return
    if (!persistedBrandId) return
    if (selectedBrandId === persistedBrandId) return
    setSelectedBrandId(persistedBrandId)
  }, [brandId, brandIdFromQuery, persistedBrandId, selectedBrandId, setSelectedBrandId])

  // Persist active brand for the logged-in user.
  useEffect(() => {
    if (!user?._id || !activeBrandId) return
    if (lastPersistedRef.current === activeBrandId) return
    lastPersistedRef.current = activeBrandId
    setUserSelectedBrandId(user._id, activeBrandId).catch(() => {
      // Best-effort preference persistence.
    })
  }, [user?._id, activeBrandId])

  return (
    <div className="h-screen flex flex-col bg-gray-50 safe-top safe-bottom">
      {/* Header with user info and logout */}
      {user && (
        <div className="sticky top-0 bg-white border-b border-gray-200 z-10 px-4 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* Porta logo - P in square block */}
              <div className="w-10 h-10 bg-gray-900 rounded flex items-center justify-center">
                <span className="text-white font-bold text-lg">P</span>
              </div>
              <div className="flex flex-col">
                <span className="text-sm text-gray-600">{user.email}</span>
                <ActiveBrandLabel brandId={activeBrandId} />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Link
                to={activeBrandId ? `/brands/${activeBrandId}/settings` : '/settings'}
                aria-label="Settings"
                className="w-9 h-9 flex items-center justify-center rounded-full text-gray-600 hover:text-blue-600 hover:bg-blue-50 transition-colors touch-target-min-44"
              >
                <IoSettingsOutline className="w-5 h-5" />
              </Link>
              <button
                onClick={async () => {
                  // Clear all React Query caches before logout
                  queryClient.clear()
                  await logout()
                  clearSelectedBrandId()
                }}
                className="w-9 h-9 flex items-center justify-center rounded-full text-blue-600 hover:text-blue-700 hover:bg-blue-50 touch-target-min-44"
                aria-label="Logout"
              >
                <IoLogOutOutline className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Main content area - scrollable, with bottom padding to clear fixed nav */}
      <div ref={scrollableRef} className="flex-1 overflow-y-auto pb-28">
        {children}
      </div>

      {/* Bottom navigation - fixed at bottom */}
      <BottomNavigation />
    </div>
  )
}
